"use client"

import * as React from "react"
import { usePathname } from "next/navigation"
import { cn } from "@sarkiai/utils"
import {
  Button,
  ThemeToggle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@sarkiai/ui"
import {
  Menu,
  Globe,
  Ghost,
  Flag,
  MoreVertical
} from "lucide-react"
import { useLanguage, useTranslations } from "@/components/I18nProvider"
import { useGhostModeChange } from "@/hooks/account/useGhostModeChange"

interface HeaderProps {
  title?: string
  onMenuClick?: () => void
  showMenuButton?: boolean
  user?: {
    name?: string | null
    email?: string | null
    image?: string | null
  }
  onSignOut?: () => void
  actions?: React.ReactNode
}

export function Header({
  title: initialTitle,
  onMenuClick,
  showMenuButton = true,
  user,
  onSignOut,
  actions,
}: HeaderProps) {
  const pathname = usePathname()
  const { locale, setLocale } = useLanguage()
  const t = useTranslations()
  const appbarT = useTranslations("appbar")
  const drawerT = useTranslations("account.drawer")
  const { ghostMode, toggleGhostMode } = useGhostModeChange()

  const handleLanguageChange = (value: string) => {
    setLocale(value)
  }

  const isChatUi = React.useMemo(() => pathname.includes("/account/chat"), [pathname])

  const appTitle = React.useMemo(() => {
    if (pathname.includes("/account/memory")) return drawerT("memory")
    if (pathname.includes("/account/cards")) return drawerT("my_cards")
    if (pathname.includes("/account/refill")) return drawerT("refill")
    if (pathname.includes("/account/profile")) return drawerT("my_profile")
    if (pathname.includes("/account/history")) return drawerT("history")
    if (pathname.includes("/account/settings")) return drawerT("settings")
    if (pathname.includes("/account/instructions")) return drawerT("instructions")
    if (pathname.includes("/account/voice-agent")) return drawerT("voice_assistant")
    if (pathname.includes("/account/orientation")) return drawerT("orientation")
    if (pathname.includes("/account/studio")) return drawerT("studio")
    if (pathname.includes("/account/chat")) return drawerT("app_name") || "SarkiAI"

    return initialTitle || appbarT("app_name")
  }, [pathname, drawerT, appbarT, initialTitle])

  const [isGhostDialogOpen, setIsGhostDialogOpen] = React.useState(false)

  return (
    <TooltipProvider delayDuration={0}>
      <header className="sticky top-0 z-30 w-full bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/40">
        <div className="flex h-14 items-center gap-4 px-4 lg:px-6">
          {/* Mobile menu button */}
          {showMenuButton && (
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden monochrome-hover text-foreground"
              onClick={onMenuClick}
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          )}

          {/* Dynamic App Title */}
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-black tracking-tight text-foreground transition-all duration-300">
              {appTitle}
            </h1>
          </div>

          <div className="flex-1" />

          {/* Actions */}
          <div className="flex items-center gap-1.5 md:gap-2">
            {actions}

            {/* Ghost Mode Toggle (Only in Chat) */}
            {isChatUi && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (!ghostMode) {
                          setIsGhostDialogOpen(true)
                        } else {
                          toggleGhostMode()
                        }
                      }}
                      className={cn(
                        "h-9 w-9 rounded-xl transition-all duration-300 hover:bg-destructive/10 hover:text-destructive dark:hover:bg-destructive/10 dark:hover:text-destructive",
                        ghostMode && "text-destructive dark:text-destructive bg-destructive/10"
                      )}
                    >
                      <Ghost className={cn("h-4 w-4" /*, ghostMode && "animate-pulse"*/)} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    {t("chat.private_mode") || "Mode Fantôme"}
                  </TooltipContent>
                </Tooltip>

                <AlertDialog
                  open={isGhostDialogOpen}
                  onOpenChange={setIsGhostDialogOpen}
                >
                  <AlertDialogContent className="rounded-[2.5rem] border-border/40 bg-background/50 backdrop-blur-xl p-4 max-w-[400px]">
                    <AlertDialogHeader className="space-y-2">
                      <AlertDialogTitle className="text-xl flex items-center tracking-tighter text-foreground leading-none">
                        <div className="h-8 w-8 mr-2 rounded-lg bg-destructive/10 flex items-center justify-center">
                          <Ghost className="h-4 w-4 text-destructive" />
                        </div>
                        {t('chat.ghost_mode_title') || "Mode Fantôme"}
                      </AlertDialogTitle>
                      <AlertDialogDescription className="text-foreground/70 leading-relaxed font-medium text-sm">
                        {t('chat.ghost_mode_info') || "En mode fantôme, la conversation n'apparaîtra pas dans vos conversations."}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-4 gap-3 flex-col sm:flex-row tracking-[0.1em] text-[10px]">
                      <AlertDialogCancel className="rounded-xl h-8 border-none bg-muted hover:bg-muted text-foreground hover:text-foreground">
                        {t('actions.cancel')}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => {
                          toggleGhostMode()
                          setIsGhostDialogOpen(false)
                        }}
                        className="rounded-xl h-8 bg-red-500 text-white hover:bg-red-600"
                      >
                        {t('actions.validate')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}

            <ThemeToggle className="monochrome-hover" />

            {/* Account and App Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl monochrome-hover text-foreground">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 p-2 backdrop-blur-xl bg-background/95 border-border/40">
                <DropdownMenuItem className="rounded-lg py-2.5 cursor-pointer focus:bg-muted text-foreground" disabled={!isChatUi}>
                  <Flag className="mr-3 h-4 w-4 text-foreground" />
                  <span className="text-xs tracking-tight text-foreground">{appbarT("report")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
    </TooltipProvider >
  )
}
